import { pixelPicture } from "./pixelArt";
import { bendOf, compose, css, emptyBounds, isEmpty, shapeBounds, transformBounds, union, type Affine, type Bend, type Bounds, type Form, type Palette, type PathOp, type Pose, type RigModel, type Shape } from "./Rig";

/**
 * Drawing a rig to a 2D canvas, in world units with y up (the caller sets
 * that transform): the whole rig posed, for the preview page; or a few
 * shapes baked to a picture, for the game's quads (see RigMesh).
 */

export function tracePath(ctx: CanvasRenderingContext2D, ops: readonly PathOp[]): void {
  for (const op of ops) {
    if (op[0] === "M") ctx.moveTo(op[1], op[2]);
    else if (op[0] === "L") ctx.lineTo(op[1], op[2]);
    else if (op[0] === "Q") ctx.quadraticCurveTo(op[1], op[2], op[3], op[4]);
    else if (op[0] === "C") ctx.bezierCurveTo(op[1], op[2], op[3], op[4], op[5], op[6]);
    else ctx.closePath();
  }
}

/** One shape in its part's frame, in `color`. */
export function drawShape(ctx: CanvasRenderingContext2D, s: Shape, color: string): void {
  const [ox, oy] = s.at ?? [0, 0];
  const f = s.form;
  ctx.beginPath();
  if (f.kind === "path") {
    ctx.save();
    ctx.translate(ox, oy);
    tracePath(ctx, f.ops);
    ctx.restore();
    ctx.fillStyle = color;
    ctx.fill();
  } else if (f.kind === "stroke") {
    ctx.save();
    ctx.translate(ox, oy);
    tracePath(ctx, f.ops);
    ctx.restore();
    ctx.strokeStyle = color;
    ctx.lineWidth = f.width;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.stroke();
  } else if (f.kind === "disc") {
    ctx.ellipse(ox, oy, f.r, f.ry ?? f.r, f.rot ?? 0, 0, Math.PI * 2);
    ctx.fillStyle = color;
    ctx.fill();
  } else if (f.kind === "capsule") {
    ctx.moveTo(ox, oy);
    ctx.lineTo(ox + f.to[0], oy + f.to[1]);
    ctx.strokeStyle = color;
    ctx.lineWidth = f.width;
    ctx.lineCap = "round";
    ctx.stroke();
  }
}

/** A shape and where it sits in the frame being drawn. */
export interface Placed {
  shape: Shape;
  affine: Affine;
}

/** A pixel-art shape: its picture, laid in its box with hard pixel edges, dimmed by the light. */
function drawPixels(ctx: CanvasRenderingContext2D, shape: Shape, f: Form & { kind: "pixels" }, affine: Affine, palette: Palette, light: number): void {
  const [ox, oy] = shape.at ?? [0, 0];
  ctx.save();
  ctx.transform(...affine);
  ctx.translate(ox + f.box.minX, oy + f.box.maxY);
  ctx.scale(1 / f.ppu, -1 / f.ppu);
  ctx.imageSmoothingEnabled = false;
  if (light < 1) ctx.filter = `brightness(${light})`;
  ctx.drawImage(pixelPicture(f, palette), 0, 0);
  ctx.restore();
}

function drawPlaced(ctx: CanvasRenderingContext2D, items: readonly Placed[], palette: Palette, glow: ReadonlySet<string>, light: number): void {
  for (const { shape, affine } of items) {
    if (shape.form.kind === "pixels") {
      drawPixels(ctx, shape, shape.form, affine, palette, light);
      continue;
    }
    const color = palette[shape.color];
    if (!color) throw new Error(`rig: no colour ${shape.color} in the palette`);
    ctx.save();
    ctx.transform(...affine);
    ctx.globalAlpha = shape.alpha ?? 1;
    if (glow.has(shape.color)) {
      ctx.globalCompositeOperation = "lighter";
      ctx.shadowColor = css(color);
      ctx.shadowBlur = 6;
      drawShape(ctx, shape, css(color));
    } else drawShape(ctx, shape, css(color, light));
    ctx.restore();
  }
}

/** The whole rig, posed and lit, back to front. */
export function drawRig(ctx: CanvasRenderingContext2D, model: RigModel, pose: Pose, palette: Palette, light = 1): void {
  const affines = model.affines(pose);
  let items: Placed[] = [];
  for (const p of model.order) {
    const bend = bendOf(p.part, pose);
    if (!bend) {
      for (const shape of p.part.shapes) items.push({ shape, affine: affines[p.index]! });
      continue;
    }
    drawPlaced(ctx, items, palette, model.glow, light);
    items = [];
    drawBent(ctx, p.part.shapes, bend, affines[p.index]!, palette, light);
  }
  drawPlaced(ctx, items, palette, model.glow, light);
}

export interface Baked {
  canvas: HTMLCanvasElement;
  /** What the picture covers, in the frame the shapes were placed in. */
  bounds: Bounds;
}

/** Shapes drawn once to a picture at `px` pixels a unit (none if there's nothing to draw). */
export function bake(items: readonly Placed[], palette: Palette, px: number, pad = 0.1, light = 1): Baked | null {
  const bounds = emptyBounds();
  for (const { shape, affine } of items) union(bounds, transformBounds(shapeBounds(shape), affine));
  if (isEmpty(bounds)) return null;
  // On whole pixels, so pixel art baked in stays square.
  bounds.minX = Math.floor((bounds.minX - pad) * px) / px;
  bounds.minY = Math.floor((bounds.minY - pad) * px) / px;
  bounds.maxX = Math.ceil((bounds.maxX + pad) * px) / px;
  bounds.maxY = Math.ceil((bounds.maxY + pad) * px) / px;
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.ceil((bounds.maxX - bounds.minX) * px));
  canvas.height = Math.max(1, Math.ceil((bounds.maxY - bounds.minY) * px));
  const ctx = canvas.getContext("2d")!;
  ctx.setTransform(px, 0, 0, -px, -bounds.minX * px, bounds.maxY * px);
  drawPlaced(ctx, items, palette, new Set(), light);
  return { canvas, bounds };
}

/** A bent part's picture, and the cells of the grid it's drawn through that have anything in them. */
interface BentPicture {
  light: number;
  px: number;
  baked: Baked | null;
  nx: number;
  ny: number;
  cells: [number, number][];
}

/** Each bent part's last picture, by its shapes. */
const bentBakes = new WeakMap<Palette, WeakMap<readonly Shape[], BentPicture>>();

/** Cells along a bent part's longer side: fine enough that the curve reads smooth. */
export const BEND_GRID = 12;

/** The bent part's picture at `px`, baked once per look and scale and kept, with its grid's non-empty cells. */
function bentPicture(shapes: readonly Shape[], palette: Palette, light: number, px: number): BentPicture {
  let byShapes = bentBakes.get(palette);
  if (!byShapes) bentBakes.set(palette, (byShapes = new WeakMap()));
  const held = byShapes.get(shapes);
  if (held && held.light === light && held.px === px) return held;
  const baked = bake(
    shapes.map((shape) => ({ shape, affine: [1, 0, 0, 1, 0, 0] })),
    palette,
    px,
    0.1,
    light,
  );
  const pic: BentPicture = { light, px, baked, nx: 1, ny: 1, cells: [] };
  if (baked) {
    // As many cells along the shorter side as keep them about square.
    const { canvas } = baked;
    const long = Math.max(canvas.width, canvas.height);
    pic.nx = Math.max(2, Math.round((BEND_GRID * canvas.width) / long));
    pic.ny = Math.max(2, Math.round((BEND_GRID * canvas.height) / long));
    const alpha = canvas.getContext("2d")!.getImageData(0, 0, canvas.width, canvas.height).data;
    for (let i = 0; i < pic.nx; i++) {
      for (let j = 0; j < pic.ny; j++) {
        // Rows of the picture run top down; cell j counts up from the bottom.
        const x0 = Math.floor((i * canvas.width) / pic.nx);
        const x1 = Math.ceil(((i + 1) * canvas.width) / pic.nx);
        const y0 = Math.floor(((pic.ny - 1 - j) * canvas.height) / pic.ny);
        const y1 = Math.ceil(((pic.ny - j) * canvas.height) / pic.ny);
        let any = false;
        for (let y = y0; y < y1 && !any; y++) for (let x = x0; x < x1 && !any; x++) any = alpha[(y * canvas.width + x) * 4 + 3]! > 0;
        if (any) pic.cells.push([i, j]);
      }
    }
  }
  byShapes.set(shapes, pic);
  return pic;
}

/**
 * A bent part's shapes (see Bend), placed by `affine`: baked to a picture
 * at about the canvas's scale, then drawn warped, a triangle at a time,
 * through a grid of cells (the empty ones skipped).
 */
function drawBent(ctx: CanvasRenderingContext2D, shapes: readonly Shape[], bend: Bend, affine: Affine, palette: Palette, light: number): void {
  const t = ctx.getTransform();
  const px = Math.max(16, Math.ceil(Math.hypot(t.a, t.b)));
  const { baked, nx, ny, cells } = bentPicture(shapes, palette, light, px);
  if (!baked) return;
  const { canvas, bounds: b } = baked;
  const w = (b.maxX - b.minX) / nx;
  const h = (b.maxY - b.minY) / ny;
  // A hair over a pixel of overlap, so no seam shows between triangles.
  const grow = 1.2 / px;
  ctx.save();
  ctx.transform(...affine);
  // Pixel art keeps its hard edges, bent or not.
  ctx.imageSmoothingEnabled = !shapes.some((s) => s.form.kind === "pixels");
  for (const [i, j] of cells) {
    const x0 = b.minX + i * w;
    const y0 = b.minY + j * h;
    const corners: [number, number][] = [
      [x0, y0],
      [x0 + w, y0],
      [x0 + w, y0 + h],
      [x0, y0 + h],
    ];
    // Only this cell's patch of the picture (and a pixel round it), not the whole thing each time.
    const sx = Math.max(0, Math.floor((x0 - b.minX) * px) - 1);
    const sy = Math.max(0, Math.floor((b.maxY - y0 - h) * px) - 1);
    const sw = Math.min(canvas.width - sx, Math.ceil(w * px) + 2);
    const sh = Math.min(canvas.height - sy, Math.ceil(h * px) + 2);
    if (sw <= 0 || sh <= 0) continue;
    for (const tri of [
      [corners[0]!, corners[1]!, corners[2]!],
      [corners[0]!, corners[2]!, corners[3]!],
    ]) {
      const to = tri.map(([x, y]) => bend.point(x, y));
      const mx = (to[0]![0] + to[1]![0] + to[2]![0]) / 3;
      const my = (to[0]![1] + to[1]![1] + to[2]![1]) / 3;
      ctx.save();
      ctx.beginPath();
      for (const [k, [x, y]] of to.entries()) {
        const d = Math.hypot(x - mx, y - my) || 1;
        const gx = x + ((x - mx) / d) * grow;
        const gy = y + ((y - my) / d) * grow;
        if (k) ctx.lineTo(gx, gy);
        else ctx.moveTo(gx, gy);
      }
      ctx.closePath();
      ctx.clip();
      // The picture's pixels to the part's rest frame, then the bend's map for this triangle.
      ctx.transform(...compose(triangleMap(tri, to), [1 / px, 0, 0, -1 / px, b.minX, b.maxY]));
      ctx.drawImage(canvas, sx, sy, sw, sh, sx, sy, sw, sh);
      ctx.restore();
    }
  }
  ctx.restore();
}

/** The affine map taking triangle `from` onto triangle `to`. */
function triangleMap(from: readonly (readonly [number, number])[], to: readonly (readonly [number, number])[]): Affine {
  const [[x0, y0], [x1, y1], [x2, y2]] = from as [[number, number], [number, number], [number, number]];
  const [[u0, v0], [u1, v1], [u2, v2]] = to as [[number, number], [number, number], [number, number]];
  const ax = x1 - x0;
  const ay = y1 - y0;
  const bx = x2 - x0;
  const by = y2 - y0;
  const det = ax * by - bx * ay;
  // Inverse of [ax bx; ay by] times the target edges.
  const ia = by / det;
  const ib = -ay / det;
  const ic = -bx / det;
  const id = ax / det;
  const pu = u1 - u0;
  const pv = v1 - v0;
  const qu = u2 - u0;
  const qv = v2 - v0;
  const a = pu * ia + qu * ib;
  const c = pu * ic + qu * id;
  const bb = pv * ia + qv * ib;
  const d = pv * ic + qv * id;
  const [e, f] = [u0 - a * x0 - c * y0, v0 - bb * x0 - d * y0];
  return [a, bb, c, d, e, f];
}
